import { useState, useEffect, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { supabase } from '../../supabaseClient'
import { getDownloadUrl } from '../../r2Client'
import {
  Film, Image, FileText, Link as LinkIcon,
  MessageSquare, Send, X, CheckCircle2, AlertCircle,
  Clock, Archive,
} from 'lucide-react'

/* ─── helpers ─── */
const GUEST_KEY = 'plutonic_guest_name'

const statusConfig = {
  in_revisione:       { label: 'In revisione', color: 'bg-amber-500/10 text-amber-400',    icon: Clock },
  approvato:          { label: 'Approvato',     color: 'bg-emerald-500/10 text-emerald-400', icon: CheckCircle2 },
  richiede_modifiche: { label: 'Modifiche',     color: 'bg-red-500/10 text-red-400',        icon: AlertCircle },
  archiviato:         { label: 'Archiviato',    color: 'bg-gray-700 text-gray-500',          icon: Archive },
}

const tipoIcon = (tipo) => {
  if (tipo === 'video')    return <Film size={16} className="text-blue-400" />
  if (tipo === 'immagine') return <Image size={16} className="text-violet-400" />
  if (tipo === 'pdf')      return <FileText size={16} className="text-amber-400" />
  return <LinkIcon size={16} className="text-gray-400" />
}

const fmtTime = (sec) => {
  if (!sec && sec !== 0) return null
  const m = Math.floor(sec / 60)
  const s = Math.floor(sec % 60)
  return `${m}:${s.toString().padStart(2, '0')}`
}

const sortCommenti = (arr) => [...arr].sort((a, b) => {
  const ta = a.timecode_sec, tb = b.timecode_sec
  if (ta == null && tb == null) return new Date(a.created_at) - new Date(b.created_at)
  if (ta == null) return 1
  if (tb == null) return -1
  if (ta !== tb) return ta - tb
  return new Date(a.created_at) - new Date(b.created_at)
})

const renderTextWithLinks = (text) => {
  if (!text) return null
  const parts = text.split(/(https?:\/\/[^\s]+)/g)
  return parts.map((part, i) =>
    /^https?:\/\//.test(part) ? (
      <a key={i} href={part} target="_blank" rel="noopener noreferrer"
        onClick={e => e.stopPropagation()}
        className="underline break-all hover:opacity-80">{part}</a>
    ) : part
  )
}

const StatusBadge = ({ status }) => {
  const cfg = statusConfig[status] || statusConfig.in_revisione
  const Icon = cfg.icon
  return (
    <span className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full ${cfg.color}`}>
      <Icon size={11} /> {cfg.label}
    </span>
  )
}

/* ─── Watermark ─── */
const Watermark = ({ nome }) => (
  <div className="absolute inset-0 pointer-events-none overflow-hidden select-none z-10">
    {Array.from({ length: 6 }).map((_, row) =>
      Array.from({ length: 4 }).map((_, col) => (
        <div
          key={`${row}-${col}`}
          className="absolute text-white/10 text-xs font-bold whitespace-nowrap"
          style={{
            top: `${row * 18 + 5}%`,
            left: `${col * 28 - 5}%`,
            transform: 'rotate(-30deg)',
            fontSize: '11px',
            letterSpacing: '0.05em',
          }}
        >
          {nome} · CONFIDENZIALE
        </div>
      ))
    )}
  </div>
)

/* ─── NamePrompt ─── */
const NamePrompt = ({ onConfirm }) => {
  const [nome, setNome] = useState('')
  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <div className="bg-gray-900 border border-gray-800 rounded-2xl w-full max-w-sm p-6 text-center">
        <div className="w-14 h-14 bg-violet-600/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <Film size={26} className="text-violet-400" />
        </div>
        <h2 className="text-white font-semibold text-lg mb-1">Benvenuto!</h2>
        <p className="text-gray-500 text-sm mb-6">
          Inserisci il tuo nome per visualizzare e commentare questa revisione.
        </p>
        <input
          autoFocus
          value={nome}
          onChange={e => setNome(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && nome.trim()) onConfirm(nome.trim()) }}
          placeholder="Il tuo nome..."
          className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-2.5 text-white placeholder-gray-500 text-sm focus:outline-none focus:border-violet-500 transition-colors mb-4"
        />
        <button
          onClick={() => { if (nome.trim()) onConfirm(nome.trim()) }}
          disabled={!nome.trim()}
          className="w-full bg-violet-600 hover:bg-violet-500 disabled:opacity-40 text-white font-medium py-2.5 rounded-xl transition-colors text-sm">
          Continua
        </button>
      </div>
    </div>
  )
}

/* ─── Main ─── */
const RevisionePubblica = () => {
  const { token } = useParams()
  const [revisione, setRevisione] = useState(null)
  const [url, setUrl] = useState(null)
  const [commenti, setCommenti] = useState([])
  const [testo, setTesto] = useState('')
  const [timecode, setTimecode] = useState(null)
  const [currentTime, setCurrentTime] = useState(0)
  const [sending, setSending] = useState(false)
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [guestName, setGuestName] = useState(() => localStorage.getItem(GUEST_KEY) || null)
  const [authUser, setAuthUser] = useState(null)
  const [authProfile, setAuthProfile] = useState(null)
  const videoRef = useRef(null)

  // Check if logged in
  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      if (data?.user) {
        setAuthUser(data.user)
        supabase.from('profiles').select('id, full_name, nickname')
          .eq('id', data.user.id).single()
          .then(({ data: p }) => setAuthProfile(p))
      }
    })
  }, [])

  // Load revision
  useEffect(() => {
    const load = async () => {
      const { data, error } = await supabase.rpc('get_revisione_pubblica', { p_token: token })
      if (error || !data || data.length === 0) { setNotFound(true); setLoading(false); return }
      const rev = data[0]
      setRevisione(rev)

      if (rev.file_r2_key) {
        getDownloadUrl(rev.file_r2_key, { revisione_token: token }).then(setUrl)
      } else if (rev.file_url) {
        setUrl(rev.file_url)
      }

      await loadCommenti(rev.id)
      setLoading(false)
    }
    load()
  }, [token]) // eslint-disable-line react-hooks/exhaustive-deps

  const loadCommenti = async (revId) => {
    const { data } = await supabase.rpc('get_commenti_pubblica', { p_revisione_id: revId })
    setCommenti(sortCommenti(data || []))
  }

  const handleConfirmName = (nome) => {
    localStorage.setItem(GUEST_KEY, nome)
    setGuestName(nome)
  }

  const visitorName = authProfile
    ? (authProfile.nickname || authProfile.full_name || 'Utente')
    : (guestName || 'Ospite')

  const handleSendComment = async () => {
    if (!testo.trim() || sending || !revisione) return
    setSending(true)
    await supabase.rpc('add_commento_pubblica', {
      p_revisione_id: revisione.id,
      p_testo: testo.trim(),
      p_timecode_sec: timecode ?? null,
      p_autore_nome: authUser ? null : visitorName,
    })
    await loadCommenti(revisione.id)
    setTesto('')
    setTimecode(null)
    setSending(false)
  }

  const handleTimecodeClick = (sec) => {
    if (videoRef.current) {
      videoRef.current.currentTime = sec
      videoRef.current.play()
    }
  }

  const needsName = !authUser && !guestName

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-16 h-16 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
            <Film size={28} className="text-gray-600" />
          </div>
          <h1 className="text-white font-bold text-xl mb-2">Revisione non trovata</h1>
          <p className="text-gray-500 text-sm">Il link potrebbe essere scaduto o non valido.</p>
        </div>
      </div>
    )
  }

  const isVideo = revisione.tipo === 'video'
  const isImage = revisione.tipo === 'immagine'
  const isPdf   = revisione.tipo === 'pdf'
  const isLink  = revisione.tipo === 'link'

  return (
    <div className="min-h-screen bg-gray-950 flex flex-col">
      {needsName && <NamePrompt onConfirm={handleConfirmName} />}

      {/* Top bar */}
      <div className="bg-gray-900 border-b border-gray-800 px-4 py-3 flex items-center gap-3 flex-shrink-0">
        <img src="/PLUTONIC_LOGO_WHITE_NOBG.png" alt="Plutonic Studio" className="h-6 w-auto object-contain flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-white font-semibold text-sm truncate">{revisione.titolo}</p>
          {revisione.progetto_titolo && (
            <p className="text-gray-500 text-xs">{revisione.progetto_titolo}</p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {tipoIcon(revisione.tipo)}
          <StatusBadge status={revisione.status} />
        </div>
      </div>

      {/* Body */}
      <div className="flex flex-1 overflow-hidden" style={{ height: 'calc(100vh - 57px)' }}>
        {/* Media */}
        <div className="flex-1 flex flex-col bg-black relative overflow-hidden">
          <div className="flex-1 flex items-center justify-center relative overflow-hidden">
            <Watermark nome={visitorName} />

            {!url && !isLink ? (
              <div className="w-10 h-10 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
            ) : isVideo ? (
              <video
                ref={videoRef}
                src={url}
                controls
                className="max-w-full max-h-full relative z-0"
                style={{ maxHeight: 'calc(100vh - 130px)' }}
                onTimeUpdate={e => setCurrentTime(e.target.currentTime)}
              />
            ) : isImage ? (
              <img src={url} alt={revisione.titolo} className="max-w-full max-h-full object-contain relative z-0" />
            ) : isPdf ? (
              <iframe src={url} title={revisione.titolo} className="w-full h-full border-0" />
            ) : isLink ? (
              <div className="text-center px-4">
                <LinkIcon size={48} className="text-gray-600 mx-auto mb-4" />
                <a href={revisione.file_url} target="_blank" rel="noopener noreferrer"
                  className="text-violet-400 hover:text-violet-300 underline text-sm break-all">
                  {revisione.file_url}
                </a>
              </div>
            ) : null}
          </div>

          {/* Timecode picker */}
          {isVideo && (
            <div className="px-4 py-2 bg-gray-900/80 flex items-center gap-3 flex-shrink-0">
              <span className="text-gray-500 text-xs">Commenta al timecode:</span>
              <button
                onClick={() => setTimecode(timecode !== null ? null : Math.floor(currentTime))}
                className={`text-xs px-2.5 py-1 rounded-lg transition-colors ${timecode !== null ? 'bg-violet-600 text-white' : 'bg-gray-800 text-gray-400 hover:text-white'}`}>
                {timecode !== null ? `⏱ ${fmtTime(timecode)}` : `Aggiungi @ ${fmtTime(Math.floor(currentTime))}`}
              </button>
            </div>
          )}

          {revisione.descrizione && (
            <div className="px-4 py-2 bg-gray-900/60 border-t border-gray-800 flex-shrink-0">
              <p className="text-gray-400 text-xs italic">"{revisione.descrizione}"</p>
            </div>
          )}
        </div>

        {/* Comments panel — hidden on mobile */}
        <div className="hidden md:flex w-80 bg-gray-900 border-l border-gray-800 flex-col flex-shrink-0">
          <div className="px-4 py-3 border-b border-gray-800 flex-shrink-0">
            <p className="text-white font-semibold text-sm flex items-center gap-2">
              <MessageSquare size={15} /> Commenti ({commenti.length})
            </p>
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
            {commenti.length === 0 && (
              <p className="text-gray-600 text-xs text-center py-6">Nessun commento ancora.</p>
            )}
            {commenti.map(c => {
              const autoreNome = c.autore_nickname || c.autore_full_name || c.autore_nome_guest || 'Ospite'
              const isGuest = !c.autore_id
              const isMe = authUser
                ? c.autore_id === authUser.id
                : (isGuest && c.autore_nome_guest === guestName)
              return (
                <div key={c.id} className={`${isMe ? 'items-end' : 'items-start'} flex flex-col gap-1`}>
                  {c.timecode_sec != null && (
                    <button
                      onClick={() => handleTimecodeClick(c.timecode_sec)}
                      className="text-[10px] text-violet-400 hover:text-violet-300 transition-colors">
                      ⏱ {fmtTime(c.timecode_sec)}
                    </button>
                  )}
                  <div className={`max-w-[85%] px-3 py-2 rounded-xl text-sm ${isMe ? 'bg-violet-600 text-white rounded-br-sm' : 'bg-gray-800 text-gray-200 rounded-bl-sm'}`}>
                    {!isMe && (
                      <p className="text-xs font-medium mb-1 text-violet-300">
                        {autoreNome}
                        {isGuest && <span className="text-gray-500 font-normal ml-1">(ospite)</span>}
                      </p>
                    )}
                    <p className="leading-relaxed break-words whitespace-pre-wrap">{renderTextWithLinks(c.testo)}</p>
                    <p className={`text-[10px] mt-1 ${isMe ? 'text-violet-200 text-right' : 'text-gray-500'}`}>
                      {new Date(c.created_at).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              )
            })}
          </div>

          <div className="px-4 py-3 border-t border-gray-800 flex-shrink-0">
            {timecode !== null && (
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs text-violet-400 bg-violet-600/10 px-2 py-1 rounded-lg">⏱ {fmtTime(timecode)}</span>
                <button onClick={() => setTimecode(null)} className="text-gray-600 hover:text-gray-400"><X size={12} /></button>
              </div>
            )}
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <p className="text-[10px] text-gray-600 mb-1">
                  Come: <span className="text-violet-400">{visitorName}</span>
                </p>
                <textarea
                  value={testo}
                  onChange={e => setTesto(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendComment() } }}
                  placeholder="Scrivi un commento..."
                  rows={1}
                  disabled={needsName}
                  className="w-full bg-gray-800 border border-gray-700 rounded-xl px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-violet-500 transition-colors resize-none disabled:opacity-40"
                  style={{ height: '38px' }}
                />
              </div>
              <button
                onClick={handleSendComment}
                disabled={!testo.trim() || sending || needsName}
                className="flex-shrink-0 w-9 h-9 bg-violet-600 hover:bg-violet-500 disabled:opacity-40 rounded-xl flex items-center justify-center transition-colors">
                <Send size={14} className="text-white" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default RevisionePubblica
